import { useEffect, useState, useRef, ComponentType } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, BellIcon, MapIcon, CheckIcon, Building2Icon, SunIcon, MoonIcon, MonitorIcon, SparklesIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface MobileHeaderProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  onOpenMap: () => void;
  onOpenAlarms: () => void;
  onOpenScenes?: () => void;
  hasAlarms?: boolean;
  onBack?: () => void;
  currentRoomName?: string;
  sensorsCount?: number;
  apartmentLabel?: string;
  themePref?: 'dark' | 'light' | 'system';
  onThemeToggle?: () => void;
  showZoneSelector?: boolean;
}
export function MobileHeader({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onOpenMap,
  onOpenAlarms,
  onOpenScenes,
  hasAlarms = false,
  onBack,
  apartmentLabel,
  themePref = 'system',
  onThemeToggle,
  showZoneSelector = true,
}: MobileHeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const defaultRoom: Room = {
    id: selectedRoomId || 'ALL',
    name: apartmentLabel || '전체 공간',
    temp: 0,
    icon: Building2Icon,
  };

  const currentRoom = rooms.find((r) => r.id === selectedRoomId) || rooms[0] || defaultRoom;

  const handleSelectRoom = (id: string) => {
    onSelectRoom(id);
    setIsDropdownOpen(false);
  };
  return (
    <div className="relative z-30">
      {/* Header Bar */}
      <div
        className="h-14 relative flex items-center justify-between px-4 backdrop-blur-md border-b"
        style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
      >
        {/* Left: Zone Dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border active:opacity-80 transition-colors min-w-0"
            style={{ backgroundColor: 'var(--mobile-bg-sub)', borderColor: 'var(--mobile-border)', color: 'var(--mobile-text-secondary)' }}
          >
            {(() => {
              const RoomIcon = currentRoom.icon;
              return <RoomIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
            })()}
            <span className="text-xs font-medium truncate max-w-[80px]" style={{ color: 'var(--mobile-text-secondary)' }}>
              {currentRoom.name}
            </span>
            <ChevronDownIcon
              className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 mt-1.5 w-48 py-1.5 rounded-xl backdrop-blur-xl border shadow-2xl shadow-black/50 overflow-hidden max-h-[60vh] overflow-y-auto"
                style={{ backgroundColor: 'var(--mobile-bg-dropdown)', borderColor: 'var(--mobile-border-strong)' }}
              >
                {rooms.map((room) => {
                  const isSelected = room.id === selectedRoomId;
                  const RoomIcon = room.icon;
                  const isAll = room.id === 'ALL';
                  return (
                    <button
                      key={room.id}
                      onClick={() => handleSelectRoom(room.id)}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 transition-colors ${
                        isSelected ? 'bg-cyan-500/15 text-cyan-300' : 'active:opacity-60'
                      } ${!isAll ? 'pl-8' : ''}`}
                      style={isSelected ? {} : { color: 'var(--mobile-text-secondary)' }}
                    >
                      <RoomIcon
                        className={`w-4 h-4 flex-shrink-0 ${
                          isSelected ? 'text-cyan-400' : 'text-[var(--mobile-text-muted)]'
                        }`}
                      />
                      <span className="text-sm font-medium flex-1 text-left">{room.name}</span>
                      {isSelected && <CheckIcon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Center: Apartment Name (Absolute Screen Centered) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none max-w-[45%] z-10">
          <h1
            className="text-[0.95rem] font-bold font-notosans tracking-tight leading-none truncate text-center"
            style={{ color: 'var(--mobile-text-primary)' }}
          >
            {apartmentLabel || 'Select Unit'}
          </h1>
        </div>
        <div className="flex-1" />

        {/* Right: Actions (Bell + Map only) */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onOpenAlarms}
            className="relative p-1.5 rounded-lg active:opacity-60 transition-colors"
            style={{ color: 'var(--mobile-text-muted)' }}
            aria-label="알람"
            title="알림 목록"
          >
            <BellIcon className="w-[1.25rem] h-[1.25rem]" />
            {hasAlarms && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full shadow-[0_0_6px_rgba(239,68,68,0.8)]" />
            )}
          </button>
          <button
            onClick={onOpenMap}
            className="p-1.5 rounded-lg active:opacity-60 transition-colors"
            style={{ color: 'var(--mobile-text-muted)' }}
            aria-label="공간 맵"
            title="도면 보기"
          >
            <MapIcon className="w-[1.25rem] h-[1.25rem]" />
          </button>
        </div>
      </div>
    </div>
  );
}